// ===== password-forgot.effects.ts =====

import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { exhaustMap, map, catchError, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

import { ApiService } from 'src/app/shared/services';
import * as PasswordRecoveryActions from './password-forgot.actions';

/**
 * Password Forgot Effects
 * Handles password recovery flow via backend API (SendGrid)
 */
@Injectable()
export class PasswordForgotEffects {
  constructor(
    private actions$: Actions,
    private api: ApiService,
    private router: Router,
    private store: Store,
    private toastr: ToastrService,
  ) {}

  /**
   * Send password recovery email via backend API
   * Note: Always returns success to prevent email enumeration attacks
   */
  createPasswordRecoveryRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PasswordRecoveryActions.passwordRecoveryRequestActions.create),
      exhaustMap(({ email }) =>
        this.api.forgotPassword(email).pipe(
          map(() =>
            PasswordRecoveryActions.passwordRecoveryRequestActions.createSuccess(
              { email },
            ),
          ),
          catchError((error: any) => {
            // Return success even on error to prevent email enumeration
            console.error('Password recovery request error:', error);
            return of(
              PasswordRecoveryActions.passwordRecoveryRequestActions.createSuccess(
                { email },
              ),
            );
          }),
        ),
      ),
    ),
  );

  /**
   * Show success message after recovery email sent
   */
  recoveryRequestSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          PasswordRecoveryActions.passwordRecoveryRequestActions.createSuccess,
        ),
        tap(({ email }) => {
          this.toastr.success(
            'If this email exists, a reset link has been sent.',
            'Check your inbox',
          );
        }),
      ),
    { dispatch: false },
  );

  /**
   * Confirm password recovery — submit new password via backend API
   */
  passwordRecoveryConfirm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PasswordRecoveryActions.passwordRecoveryConfirmActions.confirm),
      exhaustMap(({ email, sc, password }) =>
        this.api.resetPasswordRequest(email, sc, password).pipe(
          map(() =>
            PasswordRecoveryActions.passwordRecoveryConfirmActions.confirmSuccess(),
          ),
          catchError((error: any) => {
            console.error('Password recovery confirmation error:', error);
            return of(
              PasswordRecoveryActions.passwordRecoveryConfirmActions.confirmFailure(
                {
                  error:
                    error?.error?.message ||
                    error?.message ||
                    'Password reset failed',
                },
              ),
            );
          }),
        ),
      ),
    ),
  );

  /**
   * Navigate to login after successful password reset
   */
  recoveryConfirmSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          PasswordRecoveryActions.passwordRecoveryConfirmActions.confirmSuccess,
        ),
        tap(() => {
          this.toastr.success(
            'You can now sign in with your new password.',
            'Password updated!',
          );
          this.router.navigate(['/auth/login']);
        }),
      ),
    { dispatch: false },
  );

  /**
   * Handle password reset failure
   */
  recoveryConfirmFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          PasswordRecoveryActions.passwordRecoveryConfirmActions.confirmFailure,
        ),
        tap(({ error }) => {
          this.toastr.error(
            typeof error === 'string'
              ? error
              : 'Something went wrong. Please try again.',
            'Password reset failed',
          );
        }),
      ),
    { dispatch: false },
  );
}
